import Navbar from "./components/Navbar";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import React, { useEffect } from "react"; 
import { I18nProvider } from "./i18n/I18nContext";

import { AuthProvider } from "./components/context/AuthContext";

import Landing from "./components/Pages/Landing/Landing";
import Products from "./components/Products/Products";
import ProductDetail from "./components/Products/ProductDetail";
import Login from "./components/Auth/Login";
import ResetPassword from "./components/Auth/ResetPassword";

import Register from "./components/Auth/Register";
import Checkout from "./components/Checkout";
import ProfilePage from "./components/User/ProfilePage";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Users from "./components/Admin/Users";
import AdminProducts from "./components/Admin/Products";
import AdminProductForm from "./components/Admin/ProductForm";
import AdminCategories from "./components/Admin/Categories";
import AdminCoupons from "./components/Admin/Coupons";

import Orders from "./components/Orders/Orders";
import OrderDetail from "./components/Orders/OrderDetail";

import Wishlist from "./components/Wishlist/Wishes";

import { CartProvider } from "./components/context/CartContext";

import About from "./components/Pages/About/About";
import Contact from "./components/Pages/Contact/Contact";
import Complains from "./components/Pages/Complains/Complains";
import Partnerships from "./components/Pages/Partnerships/Partnerships";
import PressReleases from "./components/Pages/PressReleases/PressReleases";
import PrivacyPolicy from "./components/Pages/Policies/PrivacyPolicy";
import ReturnsPolicy from "./components/Pages/Policies/ReturnsPolicy";
import CustomerSupport from "./components/Pages/Policies/CustomerSupport";
import Installation from "./components/Pages/Services/Installation";
import Consulting from "./components/Pages/Services/Consulting";
import OurTeam from "./components/Pages/Services/OurTeam";
import Faq from "./components/Pages/Services/Faq";
import Guarantee from "./components/Pages/Services/Guarantee";
import Catalogo from "./components/Pages/catalogo/catalogo";

import ScrollToTop from "./components/ScrollToTop";
import VerifyEmail from "./components/Pages/VerifyEmail";


function AdminRoute({ children }) {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isAdmin = user?.roles?.some((r) => r.name === "admin");

  if (!token) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return children;
}

function AdminPage({ children }) {
  return <div className="pt-16">{children}</div>;
}

function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
    });
  }, [location]);

  return null;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
        <Router>
          <RouteTracker />
          <ScrollToTop />
          <Navbar />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
          />

          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/orders/:id" element={<OrderDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/complains" element={<Complains />} />
            <Route path="/partnerships" element={<Partnerships />} />
            <Route path="/press-releases" element={<PressReleases />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/returns-policy" element={<ReturnsPolicy />} />
            <Route path="/customer-support" element={<CustomerSupport />} />
            <Route path="/order-access/:id" element={<OrderDetail />} />
            <Route path="/Installation" element={<Installation />} />
            <Route path="/consulting" element={<Consulting />} />
            <Route path="/our-team" element={<OurTeam />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/guarantee" element={<Guarantee />} />
            <Route path="/verify-email/:id/:hash" element={<VerifyEmail />} />
            <Route path="/catalogo" element={<Catalogo />} />

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Navigate to="/admin/users" replace />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminPage>
                    <Users />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/products"
              element={
                <AdminRoute>
                  <AdminPage>
                    <AdminProducts />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/products/create"
              element={
                <AdminRoute>
                  <AdminPage>
                    <AdminProductForm />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/products/:id/edit"
              element={
                <AdminRoute>
                  <AdminPage>
                    <AdminProductForm />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/categories"
              element={
                <AdminRoute>
                  <AdminPage>
                    <AdminCategories />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/orders"
              element={
                <AdminRoute>
                  <AdminPage>
                    <Orders />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/coupons"
              element={
                <AdminRoute>
                  <AdminPage>
                    <AdminCoupons />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/orders/:id"
              element={
                <AdminRoute>
                  <AdminPage>
                    <OrderDetail />
                  </AdminPage>
                </AdminRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
